import type { Item } from '@/domain/Item';
import { api } from '@/lib/api';
import { mutationOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';

export interface ItemFormData {
  id?: number;
  name: string;
  description?: string;
  source?: string;
  function_name?: string;
  unrestricted?: boolean;
  allergens?: Array<string>;

  // Content fields
  found_in?: string;
  form?: string;
  supplier?: string;
  location?: string;
  benefits_textarea?: string;
  doses?: string;
  product_name?: string;
  product_code?: string;
  review_date?: string;
  lot?: string;
  labaratory?: string;
  label_claim?: string;
  result?: string;
  supplement_packaging?: string;
  table_items_names?: string;
  first_disclaimer?: string;
  lab_results_numbers?: string;
  second_disclaimer?: string;
  articles?: Array<{ source: string; description: string }>;
  benefit_ids?: Array<number>;
  strengths?: Array<{
    id?: number;
    strength: string;
    price: number;
    weight?: number | null;
    nrv_percent?: number | null;
    toDelete?: boolean;
  }>;
}

export const createAdminItemMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.adminItems.create(),
    mutationFn: async (data: ItemFormData) => {
      const payload = {
        name: data.name,
        description: data.description,
        source: data.source,
        function_name: data.function_name,
        unrestricted: data.unrestricted,
        allergens: data.allergens,
        benefit_ids: data.benefit_ids,
        strengths: data.strengths?.map((s) => ({
          strength: s.strength,
          price: s.price,
          weight: s.weight ?? null,
          nrv_percent: s.nrv_percent ?? null,
        })),
        content: {
          found_in: data.found_in,
          form: data.form,
          source: data.source,
          supplier: data.supplier,
          location: data.location,
          function: data.function_name,
          benefits_textarea: data.benefits_textarea,
          doses: data.doses,
          product_name: data.product_name,
          product_code: data.product_code,
          review_date: data.review_date,
          lot: data.lot,
          labaratory: data.labaratory,
          label_claim: data.label_claim,
          result: data.result,
          description: data.description,
          supplement_packaging: data.supplement_packaging,
          table_items_names: data.table_items_names,
          first_disclaimer: data.first_disclaimer,
          lab_results_numbers: data.lab_results_numbers,
          second_disclaimer: data.second_disclaimer,
          articles: data.articles,
        },
      };

      return await api.post('admin/items', { json: payload }).json<Item>();
    },
    onSuccess: () => {
      toast.success('Supplement created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create supplement');
    },
  });
};

export const updateAdminItemMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.adminItems.update(),
    mutationFn: async (data: ItemFormData) => {
      const payload = {
        name: data.name,
        description: data.description,
        source: data.source,
        function_name: data.function_name,
        unrestricted: data.unrestricted,
        allergens: data.allergens,
        benefit_ids: data.benefit_ids,
        strengths: data.strengths,
        content: {
          found_in: data.found_in,
          form: data.form,
          source: data.source,
          supplier: data.supplier,
          location: data.location,
          function: data.function_name,
          benefits_textarea: data.benefits_textarea,
          doses: data.doses,
          product_name: data.product_name,
          product_code: data.product_code,
          review_date: data.review_date,
          lot: data.lot,
          labaratory: data.labaratory,
          label_claim: data.label_claim,
          result: data.result,
          description: data.description,
          supplement_packaging: data.supplement_packaging,
          table_items_names: data.table_items_names,
          first_disclaimer: data.first_disclaimer,
          lab_results_numbers: data.lab_results_numbers,
          second_disclaimer: data.second_disclaimer,
          articles: data.articles,
        },
      };

      return await api
        .put(`admin/items/${data.id}`, { json: payload })
        .json<Item>();
    },
    onSuccess: () => {
      toast.success('Supplement updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update supplement');
    },
  });
};

export const deleteAdminItemMutation = () => {
  return mutationOptions({
    mutationKey: mutationKeys.adminItems.delete(),
    mutationFn: async (id: number) => {
      return await api.delete(`admin/items/${id}`).json<void>();
    },
    onSuccess: () => {
      toast.success('Supplement deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete supplement');
    },
  });
};

export interface ItemPriceFormData {
  itemId: number;
  id?: number;
  strength: string;
  price: number;
  weight?: number | null;
  nrv_percent?: number | null;
}

export const createAdminItemPriceMutation = () => {
  return {
    mutationKey: [mutationKeys.adminItems.index, 'prices', 'create'],
    mutationFn: async (data: ItemPriceFormData) => {
      return await api
        .post(`admin/items/${data.itemId}/prices`, {
          json: {
            strength: data.strength,
            price: data.price,
            weight: data.weight ?? null,
            nrv_percent: data.nrv_percent ?? null,
          },
        })
        .json<void>();
    },
  };
};

export const updateAdminItemPriceMutation = () => {
  return {
    mutationKey: [mutationKeys.adminItems.index, 'prices', 'update'],
    mutationFn: async (data: ItemPriceFormData) => {
      return await api
        .put(`admin/items/${data.itemId}/prices/${data.id}`, {
          json: {
            strength: data.strength,
            price: data.price,
            weight: data.weight ?? null,
            nrv_percent: data.nrv_percent ?? null,
          },
        })
        .json<void>();
    },
  };
};

export const deleteAdminItemPriceMutation = () => {
  return {
    mutationKey: [mutationKeys.adminItems.index, 'prices', 'delete'],
    mutationFn: async (data: { itemId: number; id: number }) => {
      return await api
        .delete(`admin/items/${data.itemId}/prices/${data.id}`)
        .json<void>();
    },
  };
};
